import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import CustomModal from "./customModal";
import useDarkMode from "../../../hooks/useDarkMode";
import useThemeColor from "../../../hooks/useThemeColor";
import showErrorToast from "../toast/toast";

export default function ProductSelectionModal({
  isOpen,
  onClose,
  onSelectProduct,
  compID,
}) {
  const navigate = useNavigate();
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const fetchProducts = useCallback(async () => {
    if (!compID || !isOpen) return;

    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_PRODUCTS_WITHOUT_RECIPES, {
        params: {
          CompID: compID,
          Search: searchTerm,
        },
      });

      const productsData = response.data.data || response.data;
      if (Array.isArray(productsData)) {
        // Filter out null/undefined values and ensure menuItem_ID exists
        const validProducts = productsData.filter(
          (product) => product != null && product.menuItem_ID != null
        );
        setProducts(validProducts);
        setFilteredProducts(validProducts);
      } else {
        setProducts([]);
        setFilteredProducts([]);
      }
    } catch (err) {
      setProducts([]);
      setFilteredProducts([]);
      showErrorToast("Failed to fetch products");
    } finally {
      setLoading(false);
    }
  }, [compID, isOpen, searchTerm]);

  useEffect(() => {
    if (isOpen) {
      fetchProducts();
    } else {
      setSearchTerm("");
      setSelectedProduct(null);
    }
  }, [isOpen, fetchProducts]);

  const getProductDisplayName = (product) => {
    if (!product) return "";
    const v = (p) => (p && String(p).trim()) || "";
    return (
      v(product.menuItem_DefaultVariantTitle) ||
      v(product.MenuItem_DefaultVariantTitle) ||
      v(product.menuItem_Name) ||
      v(product.MenuItem_Name) ||
      v(product.menuitem_name) ||
      ""
    );
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
  };

  const handleContinue = () => {
    if (selectedProduct && selectedProduct.menuItem_ID) {
      // Navigate to CreateRecipe page with the selected product's ID
      navigate(`/recipes/createRecipe/${selectedProduct.menuItem_ID}`);
      onClose(); // Close the modal
    }
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      title="Select Product"
      subtitle="Choose a product to create a recipe for"
      onSubmit={handleContinue}
      showButton={true}
      showSubmitButton={true}
      showCancelButton={true}
      submitButtonText="Continue"
      submitButtonDisabled={!selectedProduct}
      modalSize="modal-lg"
      maxHeight="600px"
      isCentered={true}
    >
      <div>
        {/* Search Bar */}
        <div className="mb-3">
          <div
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
            }}
          >
            <div
              style={{
                position: "absolute",
                left: "16px",
                zIndex: 2,
                color: isDarkMode ? "#ffffff" : "#6c757d",
                fontSize: "16px",
              }}
            >
              <i className="fa fa-search"></i>
            </div>
            <input
              type="text"
              className="form-control"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                paddingLeft: "45px",
                backgroundColor: isDarkMode ? "#1e1e1e" : "#ffffff",
                border: `1px solid ${isDarkMode ? "#404040" : "#dee2e6"}`,
                color: isDarkMode ? "#ffffff" : "#000000",
                borderRadius: "8px",
              }}
            />
          </div>
        </div>

        {/* Products List */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-5">
            <p style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>
              No products found without recipes
            </p>
          </div>
        ) : (
          <div
            style={{
              maxHeight: "400px",
              overflowY: "auto",
            }}
          >
            {filteredProducts
              .filter((product) => product != null && product.menuItem_ID != null)
              .map((product) => (
              <div
                key={product.menuItem_ID}
                onClick={() => handleProductClick(product)}
                style={{
                  padding: "12px",
                  marginBottom: "8px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  backgroundColor:
                    selectedProduct?.menuItem_ID === product.menuItem_ID
                      ? `${themeColor}20`
                      : isDarkMode
                      ? "#1e1e1e"
                      : "#f8f9fa",
                  border:
                    selectedProduct?.menuItem_ID === product.menuItem_ID
                      ? `2px solid ${themeColor}`
                      : `1px solid ${isDarkMode ? "#404040" : "#dee2e6"}`,
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  if (selectedProduct?.menuItem_ID !== product.menuItem_ID) {
                    e.currentTarget.style.backgroundColor = isDarkMode
                      ? "#2a2a2a"
                      : "#e9ecef";
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedProduct?.menuItem_ID !== product.menuItem_ID) {
                    e.currentTarget.style.backgroundColor = isDarkMode
                      ? "#1e1e1e"
                      : "#f8f9fa";
                  }
                }}
              >
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h6
                      style={{
                        margin: 0,
                        color: isDarkMode ? "#ffffff" : "#000000",
                        fontWeight: "600",
                      }}
                    >
                      {getProductDisplayName(product)}
                    </h6>
                    {product.menuItem_SKU && (
                      <small
                        style={{
                          color: isDarkMode ? "#adb5bd" : "#6c757d",
                        }}
                      >
                        SKU: {product.menuItem_SKU}
                      </small>
                    )}
                    {product.rootCat_Name && (
                      <small
                        style={{
                          color: isDarkMode ? "#adb5bd" : "#6c757d",
                          display: "block",
                        }}
                      >
                        Category: {product.rootCat_Name}
                      </small>
                    )}
                  </div>
                  {selectedProduct?.menuItem_ID === product.menuItem_ID && (
                    <i
                      className="fa fa-check-circle"
                      style={{
                        color: themeColor,
                        fontSize: "20px",
                      }}
                    ></i>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </CustomModal>
  );
}

